import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle, Warning, Info, Database, ShieldCheck, Lightning, Export, X,
  ArrowsClockwise
} from '@phosphor-icons/react';

const CATEGORY_LABELS = {
  content: 'Content',
  media: 'Media',
  seo: 'SEO',
  database: 'Database',
  security: 'Security',
  performance: 'Performance',
  integration: 'Integration',
};

const SEVERITY_CONFIG = {
  high: { color: 'text-red-600', bg: 'bg-red-50 border-red-200', icon: Warning },
  medium: { color: 'text-amber-600', bg: 'bg-amber-50 border-amber-200', icon: Info },
  low: { color: 'text-blue-600', bg: 'bg-blue-50 border-blue-200', icon: Info },
};

export default function CmsContentQuality() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');

  useEffect(() => {
    loadLastScan();
  }, []);

  const loadLastScan = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/health`, {
        withCredentials: true,
      });
      setReport(r.data);
    } catch (e) {
      if (e.response?.status !== 404) {
        toast.error('Failed to load health report');
      }
    } finally {
      setLoading(false);
    }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/health/scan`,
        {},
        { withCredentials: true }
      );
      setReport(r.data);
      toast.success(`Scan complete. Found ${r.data.total_issues} issues.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const fixIssue = async (issue) => {
    if (!issue.autofix) return;
    const action = issue.autofix;
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/health/fix`,
        {
          action,
          record_type: issue.record_type,
          record_id: issue.record_id,
        },
        { withCredentials: true }
      );
      toast.success('Fixed');
      runScan();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fix failed');
    }
  };

  const ignoreIssue = async (issue) => {
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/health/fix`,
        {
          action: 'ignore',
          record_type: issue.record_type,
          record_id: issue.record_id,
        },
        { withCredentials: true }
      );
      toast.success('Marked as ignored');
      runScan();
    } catch (e) {
      toast.error('Failed to ignore');
    }
  };

  const exportCSV = async () => {
    try {
      const r = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/health/export.csv`,
        { withCredentials: true, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'kavyalekh_health_report.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full"></div>
      </div>
    );
  }

  const filteredIssues = report?.issues?.filter((issue) => {
    if (filterCategory !== 'all' && issue.category !== filterCategory) return false;
    if (filterSeverity !== 'all' && issue.severity !== filterSeverity) return false;
    return true;
  }) || [];

  const score = report?.score?.overall || 0;
  const scoreColor = score >= 90 ? 'text-green-600' : score >= 70 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="space-y-6" data-testid="cms-content-quality">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck size={28} weight="duotone" className="text-primary" />
            Content Quality & Data Integrity
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Scan your database for missing data, orphaned references, duplicate entries, and SEO issues
          </p>
        </div>
        <div className="flex items-center gap-2">
          {report && (
            <button
              onClick={exportCSV}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm border border-border rounded-lg hover:bg-muted"
              data-testid="export-csv"
            >
              <Export size={16} /> Export CSV
            </button>
          )}
          <button
            onClick={runScan}
            disabled={scanning}
            className="inline-flex items-center gap-1.5 px-5 py-2 bg-primary text-primary-foreground rounded-lg font-semibold text-sm disabled:opacity-50"
            data-testid="run-scan"
          >
            <ArrowsClockwise size={16} weight={scanning ? 'bold' : 'regular'} />
            {scanning ? 'Scanning…' : report ? 'Re-scan' : 'Run Health Scan'}
          </button>
        </div>
      </div>

      {!report ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <ShieldCheck size={48} weight="duotone" className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground mb-4">No health report available yet.</p>
          <button
            onClick={runScan}
            disabled={scanning}
            className="inline-flex items-center gap-1.5 px-5 py-2 bg-primary text-primary-foreground rounded-lg font-semibold disabled:opacity-50"
          >
            {scanning ? 'Scanning…' : 'Run First Scan'}
          </button>
        </div>
      ) : (
        <>
          {/* Score Card */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Overall Health Score</p>
                  <p className={`text-5xl font-bold ${scoreColor}`}>{score}%</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Last scanned: {new Date(report.scanned_at).toLocaleString()}
                  </p>
                </div>
                <div className={`w-20 h-20 rounded-full flex items-center justify-center ${score >= 90 ? 'bg-green-100 text-green-600' : score >= 70 ? 'bg-amber-100 text-amber-600' : 'bg-red-100 text-red-600'}`}>
                  <CheckCircle size={40} weight="duotone" />
                </div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-6">
              <p className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Category Scores</p>
              <div className="space-y-2">
                {Object.entries(report.score.categories || {}).map(([cat, val]) => (
                  <div key={cat} className="flex items-center justify-between text-sm">
                    <span className="capitalize">{CATEGORY_LABELS[cat] || cat}</span>
                    <span className={`font-semibold ${val >= 90 ? 'text-green-600' : val >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                      {val}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3">
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="text-sm px-3 py-2 bg-card border border-border rounded-lg outline-none focus:border-primary"
              data-testid="filter-category"
            >
              <option value="all">All Categories</option>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
            <select
              value={filterSeverity}
              onChange={(e) => setFilterSeverity(e.target.value)}
              className="text-sm px-3 py-2 bg-card border border-border rounded-lg outline-none focus:border-primary"
              data-testid="filter-severity"
            >
              <option value="all">All Severities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
            <div className="text-sm text-muted-foreground ml-auto">
              {filteredIssues.length} of {report.total_issues} issues
            </div>
          </div>

          {/* Issues List */}
          {filteredIssues.length === 0 ? (
            <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
              <CheckCircle size={48} weight="duotone" className="text-green-600 mx-auto mb-3" />
              <p className="text-green-800 font-semibold">All clear! No issues found.</p>
              <p className="text-sm text-green-700 mt-1">Your site is in great shape.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="issues-list">
              {filteredIssues.map((issue) => {
                const severityConfig = SEVERITY_CONFIG[issue.severity] || SEVERITY_CONFIG.low;
                const Icon = severityConfig.icon;
                return (
                  <div
                    key={issue.id}
                    className={`border rounded-lg p-4 ${severityConfig.bg}`}
                    data-testid={`issue-${issue.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <Icon size={20} weight="duotone" className={severityConfig.color} />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm">{issue.title}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <span className="uppercase tracking-widest">{CATEGORY_LABELS[issue.category] || issue.category}</span>
                          <span>·</span>
                          <span className="capitalize">{issue.severity} priority</span>
                          <span>·</span>
                          <span className="capitalize">{issue.record_type}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {issue.autofix && (
                          <button
                            onClick={() => fixIssue(issue)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-semibold"
                            data-testid={`fix-${issue.id}`}
                          >
                            <Lightning size={12} weight="fill" /> Quick Fix
                          </button>
                        )}
                        <button
                          onClick={() => ignoreIssue(issue)}
                          className="text-muted-foreground hover:text-foreground p-1"
                          title="Ignore this issue"
                          data-testid={`ignore-${issue.id}`}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
